import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environment/environment';
import { filter, firstValueFrom, map, Subject, take } from 'rxjs';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class LanguagesService {
  private readonly LANG_KEY = 'lang';
  private readonly SYNC_KEY = 'lang_synced';
  private languageChangeSubject = new Subject<'ar' | 'en'>();
  languageChanged$ = this.languageChangeSubject.asObservable();

  constructor(
    private translate: TranslateService,
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.translate.setDefaultLang('ar');
    this.applyLanguageLocally(this.getCurrentLanguage());
  }

  getCurrentLanguage(): 'ar' | 'en' {
    const lang = localStorage.getItem(this.LANG_KEY);
    return lang === 'en' || lang === 'ar' ? lang : 'ar';
  }

  setCurrentLanguage(lang: 'ar' | 'en'): void {
    const token = localStorage.getItem('token');
    const alreadySynced = localStorage.getItem(this.SYNC_KEY);

    const currentUrl$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.router.url)
    );

    const queryParams$ = this.route.queryParams;

    queryParams$.subscribe(params => {
      const isPaperInvestor =
        params['data'] || params['signature'] || params['expires'];

      if (isPaperInvestor) {
        window.location.reload();
      }
    });

    currentUrl$.subscribe(currentUrl => {
      const isAuthRoute =
        currentUrl.includes('auth') && !currentUrl.includes('kyc');

      if (isAuthRoute) {
        this.applyLanguageLocally(lang);
      }
    });

    if (token && alreadySynced !== lang) {
      this.syncLanguageWithServer(lang);
    } else {
      this.applyLanguageLocally(lang);
    }
  }

  setLanguageToLocalStorage(lang: string) {
    localStorage.setItem(this.LANG_KEY, lang);
  }

  private applyLanguageLocally(lang: 'ar' | 'en') {
    localStorage.setItem(this.LANG_KEY, lang);
    this.translate
      .use(lang)
      .pipe(take(1))
      .subscribe(() => {
        document.documentElement.lang = lang;
        document.documentElement.dir = lang === 'en' ? 'ltr' : 'rtl';
        this.languageChangeSubject.next(lang);
      });
  }

  private syncLanguageWithServer(lang: 'ar' | 'en'): Promise<void> {
    localStorage.setItem(this.LANG_KEY, lang);
    return firstValueFrom(
      this.http.put(`${environment.baseUrl}api/v1/investor/language`, null)
    )
      .then(() => {
        localStorage.setItem(this.SYNC_KEY, lang);
        this.applyLanguageLocally(lang);
        this.router.navigate(['/dashboard']);
      })
      .catch(() => {
        this.applyLanguageLocally(lang);
      });
  }
}
